/**
 * Componente: ConsultationGrowthChart
 * Gráfico de crescimento de consultas (telemedicina vs presencial)
 */

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Loader2 } from 'lucide-react';

export interface ConsultationGrowthData {
  month: string;
  telemedicine: number;
  domiciliary: number;
}

interface ConsultationGrowthChartProps {
  data?: ConsultationGrowthData[];
  isLoading?: boolean;
}

const formatMonthLabel = (dateStr: string) => {
  if (!dateStr || dateStr.length < 7) return dateStr;
  const year = dateStr.substring(0, 4);
  const monthNum = dateStr.substring(5, 7);
  const monthMap: Record<string, string> = {
    '01': 'JAN', '02': 'FEB', '03': 'MAR', '04': 'APR',
    '05': 'MAY', '06': 'JUN', '07': 'JUL', '08': 'AUG',
    '09': 'SEP', '10': 'OCT', '11': 'NOV', '12': 'DEC'
  };
  return monthMap[monthNum] ? `${monthMap[monthNum]}/${year.substring(2)}` : dateStr;
};

export function ConsultationGrowthChart({ data = [], isLoading = false }: ConsultationGrowthChartProps) {
  const formattedData = data.map(item => ({
    ...item,
    formattedMonth: formatMonthLabel(item.month)
  }));

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Crescimento de Consultas
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Telemedicina vs Domiciliar (Baseado nos agendamentos)
        </p>
      </div>

      <div className="h-80">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
          </div>
        ) : formattedData.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-sm text-gray-500">Sem dados para exibir.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={formattedData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e5e7eb"
                className="dark:stroke-neutral-700"
              />
              <XAxis
                dataKey="formattedMonth"
                stroke="#9ca3af"
                className="dark:stroke-neutral-500"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#9ca3af"
                className="dark:stroke-neutral-500"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: any) => `${value}`}
              />
              <Legend
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="line"
              />
              <Line
                type="monotone"
                dataKey="telemedicine"
                stroke="#2b9fa0"
                strokeWidth={2}
                dot={{ fill: '#2b9fa0', r: 4 }}
                activeDot={{ r: 6 }}
                name="Telemedicina"
              />
              <Line
                type="monotone"
                dataKey="domiciliary"
                stroke="#cbd5e1"
                strokeWidth={2}
                dot={{ fill: '#cbd5e1', r: 4 }}
                activeDot={{ r: 6 }}
                name="Domiciliar"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

